import { cloneDeep, set } from "lodash";
import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import { styled } from "@mui/material/styles";
import { Modal, Tab, Tabs } from "@mui/material";
import "./style.scss";
import {
  MESSAGE_TYPE,
  COMPONENT_MODES,
  CTA_SIZE,
  CTA_TYPE,
} from "../../../data/constants";
import {
  getAllTaxAreasData,
  getAllTaxTypesData,
  saveTaxAreaData,
  saveTaxTypeData,
  updateTaxAreasMap,
  updateTaxTypesMap,
} from "../../../redux/slices";
import TaxAreaViewComponent from "./taxArea-view";
import TaxAreaDetailsForm from "./taxArea-details-form";
import { isAddNewMode, isEditMode } from "../../../helpers/catalog-helper";
import TaxTypeViewComponent from "./taxType-view";
import TaxTypeDetailsForm from "./taxType-details-form";
import CtaComponent from "../../../components/reusable-ui/cta";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import DescriptionText from "../../../components/reusable-ui/description-text";
import { CMS_DESCRIPTION_TEXTS } from "../../../data/cms-description-text";

const AntTabs = styled(Tabs)({
  borderBottom: "1px solid #ECECEC",
  "& .MuiTabs-indicator": {
    backgroundColor: "#0074FB",
    height: "2px",
  },
});

const TaxesManagenent: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const TAX_TABS: any = [
    { id: "taxArea", text: "Tax Area" },
    { id: "taxName", text: "Tax Name" },
  ];
  const [selectedTab, setSelectedTab] = useState<string>(TAX_TABS[0].id);
  const [modeConfig, setModeConfig] = useState<any>({
    //it will determine whether it's in view mode or edit mode
    type: COMPONENT_MODES.viewMode,
    tabId: "",
  });
  const [taxAreaId, setTaxAreaId] = useState<string>("");
  const [taxTypeId, setTaxTypeId] = useState<string>("");
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMessageMap, setErrorMsgMap] = useState<any>({});
  const taxAreaDataMap = useSelector(
    (state: RootState) => state.taxesReducer.taxAreaDataMap
  );
  const taxTypeDataMap = useSelector(
    (state: RootState) => state.taxesReducer.taxTypeDataMap
  );
  const [curTaxAreaDetails, setCurTaxAreaDetails] = useState<any>({});
  const [curTaxTypeDetails, setCurTaxTypeDetails] = useState<any>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllTaxAreasData());
    dispatch(getAllTaxTypesData());
  }, []);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && taxAreaId) {
      setCurTaxAreaDetails(taxAreaDataMap[taxAreaId]);
    }
  }, [taxAreaId, taxAreaDataMap]);

  useEffect(() => {
    if (!isAddNewMode(modeConfig?.type) && taxTypeId) {
      setCurTaxTypeDetails(taxTypeDataMap[taxTypeId]);
    }
  }, [taxTypeId, taxTypeDataMap]);

  useEffect(() => {
    setTaxAreaId("");
    setTaxTypeId("");
    setCurTaxAreaDetails({});
    setCurTaxTypeDetails({});
    resetStateOnClose();
  }, [selectedTab]);

  useEffect(() => {
    if (isAddNewMode(modeConfig.type)) {
      setCurTaxTypeDetails({});
      setCurTaxAreaDetails({});
    }
  }, [modeConfig]);

  useEffect(() => {
    if (!taxAreaId) {
      setCurTaxAreaDetails({});
    }
  }, [taxAreaId]);

  useEffect(() => {
    if (!taxTypeId) {
      setCurTaxTypeDetails({});
    }
  }, [taxTypeId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedTab(newValue);
  };

  function renderHeaderTabs(): any {
    return (
      <AntTabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="catalogue-tab"
        className="px-8 sm:left-[0px] z-[11] w-full border-b-[1px] #ECECEC"
      >
        {TAX_TABS.map((item: { text: string; id: string }) => (
          <Tab
            label={item.text}
            key={item.id}
            value={item.id}
            className={`${
              item.id === selectedTab
                ? "font-semibold text-[#0074FB]"
                : "font-normal text-black/50"
            } capitalize text-16`}
          />
        ))}
      </AntTabs>
    );
  }

  const updateTaxAreaDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(curTaxAreaDetails) || {};
    set(dataToUpdate, key, value);
    setCurTaxAreaDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  const updateTaxTypeDataInParentState = (key: string, value: any) => {
    const dataToUpdate = cloneDeep(curTaxTypeDetails) || {};
    set(dataToUpdate, key, value);
    setCurTaxTypeDetails(dataToUpdate);
    setIsChangesMade(true);
  };

  function resetStateOnClose() {
    setErrorMsgMap({});
    setIsChangesMade(false);
    setModeConfig({ type: COMPONENT_MODES.viewMode });
  }

  const onTaxAreaDiscardCtaClick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (taxAreaId) {
        setCurTaxAreaDetails(cloneDeep(taxAreaDataMap[taxAreaId]));
      } else {
        setCurTaxAreaDetails({});
      }
    }
  };

  const onTaxTypeDiscardCtaClick = () => {
    resetStateOnClose();
    if (!isAddNewMode(modeConfig?.type)) {
      if (taxTypeId) {
        setCurTaxTypeDetails(cloneDeep(taxTypeDataMap[taxTypeId]));
      } else {
        setCurTaxTypeDetails({});
      }
    }
  };

  const uniqueTaxAreaName = () => {
    if (!taxAreaDataMap) return true;
    let taxAreaData: any = {};
    for (taxAreaData of Object.values(taxAreaDataMap)) {
      if (
        taxAreaData?.mid !== curTaxAreaDetails?.mid &&
        taxAreaData?.name === curTaxAreaDetails?.name
      ) {
        return false;
      }
    }
    return true;
  };

  const validateTaxAreaDetails = (taxAreaDetails: any) => {
    let isUniqueTextArea = uniqueTaxAreaName();
    if (
      taxAreaDetails?.name &&
      taxAreaDetails?.taxes?.length &&
      isUniqueTextArea
    ) {
      return { isValid: true, dataToPost: taxAreaDetails };
    }

    let errorMsgMap: any = {};

    if (!taxAreaDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }
    if (!isUniqueTextArea) {
      errorMsgMap.name = "Name should be unique";
    }
    if (!taxAreaDetails?.taxes?.length) {
      errorMsgMap.taxes = "Please add at least one tax.";
    }
    return { isValid: false, errorMsgMap };
  };

  const onTaxAreaSaveCtaClick = () => {
    const validatedData: any = validateTaxAreaDetails(curTaxAreaDetails);
    const callback = (response: any) => {
      if (isEditMode(modeConfig?.type)) {
        dispatch(updateTaxAreasMap({ key: taxAreaId, data: response }));
        setSuccessModalText("Changes Saved Successfully");
      } else if (isAddNewMode(modeConfig?.type)) {
        dispatch(getAllTaxAreasData());
        setSuccessModalText("Tax Area Created Successfully");
      }
      setShowSuccessModal(true);
      resetStateOnClose();
    };
    if (validatedData?.isValid) {
      dispatch(
        saveTaxAreaData({
          dataToPost: curTaxAreaDetails,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  const uniqueTaxTypeName = () => {
    if (!taxTypeDataMap) return true;
    let taxTypeData: any = {};
    for (taxTypeData of Object.values(taxTypeDataMap)) {
      if (
        taxTypeData?.mid !== curTaxTypeDetails?.mid &&
        taxTypeData?.name === curTaxTypeDetails?.name
      ) {
        return false;
      }
    }
    return true;
  };

  const validateTaxTypeDetails = (taxTypeDetails: any) => {
    let isUniqueTextType = uniqueTaxTypeName();

    if (taxTypeDetails?.name && isUniqueTextType) {
      return { isValid: true, dataToPost: taxTypeDetails };
    }
    let errorMsgMap: any = {};

    if (!taxTypeDetails?.name) {
      errorMsgMap.name = "This is mandatory field";
    }

    if (!isUniqueTextType) {
      errorMsgMap.name = "Name should be unique";
    }
    return { isValid: false, errorMsgMap };
  };

  const onTaxTypeSaveCtaClick = () => {
    const validatedData: any = validateTaxTypeDetails(curTaxTypeDetails);
    const callback = (response: any) => {
      if (!response?.code) {
        if (isEditMode(modeConfig?.type)) {
          dispatch(updateTaxTypesMap({ key: taxTypeId, data: response }));
          setSuccessModalText("Changes Saved Successfully");
        } else if (isAddNewMode(modeConfig?.type)) {
          dispatch(getAllTaxTypesData());
          setSuccessModalText("Tax Name Created Successfully");
        }
        setShowSuccessModal(true);
        resetStateOnClose();
      }
    };
    if (validatedData?.isValid) {
      dispatch(
        saveTaxTypeData({
          dataToPost: validatedData.dataToPost,
          callback,
        })
      );
    } else {
      setErrorMsgMap(validatedData?.errorMsgMap);
    }
  };

  const renderTaxListView = () => {
    switch (selectedTab) {
      case "taxArea":
        return (
          <TaxAreaViewComponent
            openTaxAreaView={setTaxAreaId}
            taxAreaDataMap={taxAreaDataMap || {}}
            taxAreaId={taxAreaId}
            modeConfig={modeConfig}
            setModeConfig={setModeConfig}
          />
        );
      case "taxName":
        return (
          <TaxTypeViewComponent
            openTaxTypeView={setTaxTypeId}
            taxTypeDataMap={taxTypeDataMap || {}}
            taxTypeId={taxTypeId}
            modeConfig={modeConfig}
            setModeConfig={setModeConfig}
          />
        );

      default:
    }
  };

  const renderTaxEditView = () => {
    switch (selectedTab) {
      case "taxArea":
        return taxAreaId ? (
          <TaxAreaDetailsForm
            data={curTaxAreaDetails}
            modeConfig={modeConfig}
            setModeConfig={setModeConfig}
            updateDataInParentState={updateTaxAreaDataInParentState}
            errorMessageMap={errorMessageMap}
            taxAreaDataMap={taxAreaDataMap}
            taxTypeDataMap={taxTypeDataMap}
            taxAreaId={taxAreaId}
            isChangesMade={isChangesMade}
            onDiscardCtaClick={onTaxAreaDiscardCtaClick}
            onSaveCtaClick={onTaxAreaSaveCtaClick}
          />
        ) : null;
      case "taxName":
        return taxTypeId ? (
          <TaxTypeDetailsForm
            data={curTaxTypeDetails}
            modeConfig={modeConfig}
            setModeConfig={setModeConfig}
            updateDataInParentState={updateTaxTypeDataInParentState}
            errorMessageMap={errorMessageMap}
            taxTypeDataMap={taxTypeDataMap}
            taxTypeId={taxTypeId}
            isChangesMade={isChangesMade}
            onDiscardCtaClick={onTaxTypeDiscardCtaClick}
            onSaveCtaClick={onTaxTypeSaveCtaClick}
          />
        ) : null;

      default:
    }
  };

  const renderAddNewTaxNameModal = () => {
    return (
      <Modal open={true} onClose={onTaxTypeDiscardCtaClick}>
        <div className="absolute w-[636px] h-[668px] rounded-[8px] overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white">
          <div className="flex items-center justify-between pt-[16px] pl-[24px] pr-[12px]">
            <h1 className="text-[22px] font-semibold">Add New Tax Name</h1>
            <div
              className="rista-sprite ic-cross-black"
              onClick={onTaxTypeDiscardCtaClick}
            ></div>
          </div>
          <div className="w-[636px] h-[576px] overflow-y-auto">
            <TaxTypeDetailsForm
              data={curTaxTypeDetails}
              modeConfig={modeConfig}
              setModeConfig={setModeConfig}
              updateDataInParentState={updateTaxTypeDataInParentState}
              errorMessageMap={errorMessageMap}
              taxTypeDataMap={taxTypeDataMap}
              taxTypeId={taxTypeId}
              isChangesMade={isChangesMade}
              onDiscardCtaClick={onTaxTypeDiscardCtaClick}
              onSaveCtaClick={onTaxTypeSaveCtaClick}
            />
          </div>
          <div className="absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px] px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden sm:w-full">
            <CtaComponent
              ctaText={"Discard"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onTaxTypeDiscardCtaClick}
              customClass={"w-[96px] mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onTaxTypeSaveCtaClick}
              customClass={"w-[80px] ml-10"}
              isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        </div>
      </Modal>
    );
  };

  const renderAddNewTaxAreaModal = () => {
    return (
      <Modal open={true} onClose={onTaxAreaDiscardCtaClick}>
        <div className="absolute w-[636px] h-[668px] rounded-[8px] overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white">
          <div className="flex items-center justify-between pt-[16px] pl-[24px] pr-[12px]">
            <h1 className="text-[22px] font-semibold">Add New Tax Area</h1>
            <div
              className="rista-sprite ic-cross-black"
              onClick={onTaxAreaDiscardCtaClick}
            ></div>
          </div>
          <div className="w-[636px] h-[576px] overflow-y-auto">
            <TaxAreaDetailsForm
              data={curTaxAreaDetails}
              modeConfig={modeConfig}
              setModeConfig={setModeConfig}
              updateDataInParentState={updateTaxAreaDataInParentState}
              errorMessageMap={errorMessageMap}
              taxAreaDataMap={taxAreaDataMap}
              taxTypeDataMap={taxTypeDataMap}
              taxAreaId={taxAreaId}
              isChangesMade={isChangesMade}
              onDiscardCtaClick={onTaxAreaDiscardCtaClick}
              onSaveCtaClick={onTaxAreaSaveCtaClick}
            />
          </div>
          <div className="absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px] px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden sm:w-full">
            <CtaComponent
              ctaText={"Discard"}
              ctaType={CTA_TYPE.secondary_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onTaxAreaDiscardCtaClick}
              customClass={"w-[96px] mr-10"}
            ></CtaComponent>
            <CtaComponent
              ctaText={"Save"}
              ctaType={CTA_TYPE.active_cta}
              ctaSize={CTA_SIZE.medium}
              onCtaClick={onTaxAreaSaveCtaClick}
              customClass={"w-[80px] ml-10"}
              isDisabled={!isChangesMade}
            ></CtaComponent>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <section className="flex sm:flex-col taxesManageContainer h-[calc(100vh-141px)] overflow-y-hidden">
      <section className="taxes-view-container w-[50%] bg-white pb-[24px]">
        <DescriptionText
          descriptionText={CMS_DESCRIPTION_TEXTS.masterLevel.taxes}
          customClass="text-14 text-black/50 m-[24px] mb-8"
        />
        {renderHeaderTabs()}
        {renderTaxListView()}
      </section>

      {renderTaxEditView()}

      {isAddNewMode(modeConfig.type) &&
        (selectedTab === "taxName"
          ? renderAddNewTaxNameModal()
          : renderAddNewTaxAreaModal())}

      {showSuccessModal && (
        <ApiStateModal
          modalType={MESSAGE_TYPE.success}
          open={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          mainText={successModalText}
        ></ApiStateModal>
      )}
    </section>
  );
};

export default TaxesManagenent;
